import { colorGray } from '@atlas-frontend/design-tokens';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Flex } from 'antd';
import { FC, useMemo } from 'react';
import * as Icon from 'react-feather';
import {
  renderDefaultCell,
  TableOperationButton,
  Table,
  useUserRolesQuery,
  UserDirectoryDto,
  DirectoryTableProps,
  useIsAdmin,
  useIsSuperadmin,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserTable.translations.json';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';

const { addTranslations, useTranslation } = i18nNsFactory('user-table');
addTranslations(translations);

const columnHelper = createColumnHelper<UserDirectoryDto>();

export interface UserTableProps extends DirectoryTableProps<UserDirectoryDto> {
  renderData?: (
    key: string,
    value: any,
    data: UserDirectoryDto,
    index: number,
  ) => React.ReactNode;
}

export const UserTable: FC<UserTableProps> = (props) => {
  const { renderData, ...rest } = props;
  const { t } = useTranslation();
  const { data: roles = [] } = useUserRolesQuery();

  const navigateToUserCreate = useNavigateTo(path.userCreate);

  const isAdmin = useIsAdmin();
  const isSuperadmin = useIsSuperadmin();

  const cols = useMemo(() => {
    const getCommonColumnProps = (
      key: string,
    ): Partial<ColumnDef<UserDirectoryDto, any>> => ({
      header: t(`column.${key}`),
      cell: (props) =>
        renderData?.(
          key,
          props.renderValue()?.toString() ?? '',
          props.row.original,
          props.row.index,
        ) || renderDefaultCell<UserDirectoryDto>(props),
      enableColumnFilter: true,
    });

    return [
      columnHelper.accessor('username', {
        ...getCommonColumnProps('username'),
        meta: {
          width: '25ch',
          className: 'sticky-left',
        },
      }),
      columnHelper.accessor('firstName', {
        ...getCommonColumnProps('firstName'),
        meta: { width: '25ch' },
      }),
      columnHelper.accessor('lastName', {
        ...getCommonColumnProps('lastName'),
        meta: { width: '25ch' },
      }),
      columnHelper.accessor('roles', {
        ...getCommonColumnProps('roles'),
        filterFn: 'includesOption',
        meta: { minWidth: 150 },
      }),
      columnHelper.accessor('validPeriod', {
        ...getCommonColumnProps('validPeriod'),
        filterFn: 'validPeriod',
        meta: {
          width: 180,
        },
      }),
      columnHelper.accessor('isAdmin', {
        ...getCommonColumnProps('isAdmin'),
        filterFn: 'includesOption',
        meta: {
          width: 180,
        },
      }),
      columnHelper.accessor('isSuperadmin', {
        ...getCommonColumnProps('isSuperadmin'),
        filterFn: 'includesOption',
        meta: {
          width: 180,
        },
      }),
      columnHelper.display({
        id: 'operations',
        header:
          isAdmin || isSuperadmin
            ? () => {
                const title = t('button.add-user');
                return (
                  <Flex justify="center" gap="small">
                    <TableOperationButton
                      id="create-user"
                      icon={<Icon.Plus size={22} color={colorGray[7]} />}
                      onClick={() => navigateToUserCreate()}
                      title={title}
                    />
                  </Flex>
                );
              }
            : undefined,
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          width: 50,
          className: 'sticky-right',
        },
      }),
    ];
  }, [isAdmin, isSuperadmin, t, renderData, navigateToUserCreate]);

  const options = useMemo(
    () => ({
      roles,
      isAdmin: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
      ],
      isSuperadmin: [
        { id: true, name: t('yes') },
        { id: false, name: t('no') },
      ],
    }),
    [roles, t],
  );

  return (
    <Table
      name="user-directory-table"
      columns={cols}
      hideClearFilters
      manualFiltering
      manualSorting
      options={options}
      scrollContainerSelector="#user-directory-content"
      sticky
      virtual
      {...rest}
    />
  );
};
